// Safe localStorage wrapper: never throws, always falls back gracefully.
// Handles unavailable storage (private browsing, disabled storage, SSR) and
// malformed / tampered JSON without crashing the app.

let cachedAvailability = null;

function isStorageAvailable() {
  if (cachedAvailability !== null) return cachedAvailability;

  try {
    const testKey = "__webpreview_storage_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    cachedAvailability = true;
  } catch {
    cachedAvailability = false;
  }

  return cachedAvailability;
}

/**
 * Reads a value from localStorage.
 * @param {string} key
 * @param {*} fallback - returned when the key is missing, storage is unavailable,
 *   parsing fails, or `validate` rejects the parsed value.
 * @param {(value: unknown) => boolean} [validate] - optional shape check.
 * @param {(value: unknown) => unknown} [sanitize] - optional cleanup applied to
 *   a value that passed `validate` (e.g. dropping malformed list entries). Its
 *   return value is what callers receive, never the raw parsed JSON.
 */
export function readStorage(key, fallback, validate, sanitize) {
  if (!isStorageAvailable()) return fallback;

  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;

    const parsed = JSON.parse(raw);
    if (typeof validate === "function" && !validate(parsed)) {
      return fallback;
    }
    return typeof sanitize === "function" ? sanitize(parsed) : parsed;
  } catch {
    return fallback;
  }
}

/**
 * Writes a value to localStorage. Returns true on success, false otherwise
 * (storage unavailable, quota exceeded, value not serializable).
 */
export function writeStorage(key, value) {
  if (!isStorageAvailable()) return false;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function removeStorage(key) {
  if (!isStorageAvailable()) return false;

  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export { isStorageAvailable };
